import { LoadingSpinner } from '@web_opxp/components';
import { LanguageSelectors, StatsSelectors, usePushMessages } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { AppSelectors } from '../app';
import BannerCarousel from '../bannerCarousel';
import { NEWS_LETTER_STAT_ID } from '../base/constants';
import ENV from '../base/env';
import MoodTracker from '../components/MoodTracker/MoodTracker';
import MoodTrackerWrapper from '../components/MoodTracker/MoodTrackerWrapper';
import { CyclesSelectors } from '../cycles';
import Day0Banner from '../d0banner/Day0Banner';
import ChallengeLeaderboard from '../highscores/components/ChallengeLeaderboard';
import { RestoreBanner } from '../restoreBanner';
import { SessionSelectors } from '../session';
import { IState } from '../store/state';
import { WelcomeBanner } from '../welcomeTour';
import AssessmentRunningSection from './AssessmentRunningSection';
import CycleCompletedSection from './CycleCompletedSection';
import SessionOverviewSideStorySection from './SessionOverviewSideStorySection';
import SessionSection from './SessionSection';
import { DashboardActions } from './slice';

export const DashboardRoute = '/dashboard';

export interface IDashboardProps {
  appLoaded: boolean;
  assessmentIsRunning: boolean;
  cycleCompleted: boolean;
  domain: number;
  localeLoaded: boolean;
  cyclesLoaded: boolean;
  sessionLoaded: boolean;
  cfqRunning: boolean;
  subscribedToNewsletter: boolean;
  onNewletterSubscribe: () => void;
  onOpen: () => void;
  onMoodTracking: (result: number) => void;
}

export const DashboardView: React.FC<IDashboardProps> = ({
  appLoaded,
  assessmentIsRunning,
  cycleCompleted,
  domain,
  localeLoaded,
  cyclesLoaded,
  sessionLoaded,
  cfqRunning,
  subscribedToNewsletter,
  onNewletterSubscribe,
  onOpen,
  onMoodTracking,
}) => {
  usePushMessages();
  React.useEffect(() => {
    if (appLoaded) {
      onOpen();
    }
  }, [appLoaded, onOpen]);
  if (!appLoaded || !localeLoaded || !cyclesLoaded || !sessionLoaded)
    return <LoadingSpinner assetsUrl={ENV.AssetsBaseUrl} />;

  let dashboardSection: React.ReactNode = null;
  if (assessmentIsRunning) {
    dashboardSection = <AssessmentRunningSection cfqRunning={cfqRunning} />;
  } else if (cycleCompleted) {
    dashboardSection = <CycleCompletedSection />;
  } else {
    dashboardSection = <SessionSection />;
  }
  return (
    <>
      <BannerCarousel>
        <Day0Banner />
        <RestoreBanner />
        <WelcomeBanner />
      </BannerCarousel>
      {dashboardSection}
      <MoodTrackerWrapper>
        <MoodTracker onMoodTracking={onMoodTracking} visible={true} />
      </MoodTrackerWrapper>
      {!cycleCompleted && !assessmentIsRunning ? <ChallengeLeaderboard /> : null}
      <SessionOverviewSideStorySection
        domain={domain}
        visible={!cycleCompleted && !assessmentIsRunning}
        subscribedToNewsletter={subscribedToNewsletter}
        onNewletterSubscribe={onNewletterSubscribe}
      />
    </>
  );
};

export const mapStateToProps = (state: IState) => {
  // wait for app loaded to ensure userId and session token are available
  const appLoaded = !AppSelectors.isAppLoading(state);
  const localeLoaded = !LanguageSelectors.isLoading(state);
  const cyclesLoaded = !CyclesSelectors.isCyclesLoading(state);
  const sessionLoaded = !SessionSelectors.isLoading(state);
  const domain = SessionSelectors.getSessionDomain(state);
  const cycleCompleted = CyclesSelectors.isCycleCompleted(state);
  const assessmentIsRunning = CyclesSelectors.isAssessmentRunning(state);
  const cfqRunning = CyclesSelectors.cfqWorkoutsStartedInCurrentSession(state);
  const subscribedToNewsletter = !!StatsSelectors.getStatBooleanValueById(state, NEWS_LETTER_STAT_ID);
  return {
    appLoaded,
    assessmentIsRunning,
    cycleCompleted,
    domain,
    localeLoaded,
    cyclesLoaded,
    sessionLoaded,
    cfqRunning,
    subscribedToNewsletter,
  };
};

export const mapDispatchToProps = {
  onOpen: DashboardActions.openDashboard,
  onMoodTracking: DashboardActions.saveMood,
  onNewletterSubscribe: DashboardActions.subscribeToNewsletter,
};

const Dashboard = connect(mapStateToProps, mapDispatchToProps)(DashboardView);
export default Dashboard;
